 
import { numeralFormatterCurrency, percentFormatter } from 'ns_libs/formatter';
import RenderIf from 'components/RenderIf/RenderIf';
import { IProjectSourcesPeriod } from 'views/ProFormaTable/types';
import classNames from 'classnames';
import { useProFormaSocketContext } from '../../../socketContext/ProFormaSocketProvider';
import { getSourcesPeriodDisplayValue } from '../helpers';
import PeriodCellValue from '../../DevelopmentUsesTable/PeriodCellValue/PeriodCellValue';

export interface IProjectActualsSourceRowProps {
    sourcesPeriods: IProjectSourcesPeriod[];
    traunchIndex: number;
    sourceIndex?: number;
}

const ProjectActualsSourceRow = ({
    sourcesPeriods, traunchIndex, sourceIndex,
}: IProjectActualsSourceRowProps) => {
    const { showCents } = useProFormaSocketContext();
    const showCentsFormat = showCents ? '$0,0.00' : '$0,0';

    const isSource = typeof sourceIndex === 'number';

    // starting from index 1 because index 0 is not a real data, extra period was added on the backend for calculations purposes
    const firstPeriodTraunchData = sourcesPeriods[1]?.sources?.[traunchIndex];
    let firstPeriodSource;
    if (isSource && firstPeriodTraunchData) {
        firstPeriodSource = firstPeriodTraunchData.sources?.[sourceIndex];
    }

    const inCurrentPeriodIndex = sourcesPeriods.findIndex(period => period.inCurrentPeriod);

    const { periodCadence } = useProFormaSocketContext();

    const getPeriodCells = () => sourcesPeriods.map((period, periodIndex) => {
        const traunch = period.sources?.[traunchIndex];
        if (!traunch) return null;

        let fundedValue = traunch.amountFunded || 0;
        let projectionValue = traunch.amountToFund || 0;

        let source;
        if (isSource) {
            source = traunch.sources?.[sourceIndex];
            fundedValue = source?.amountFunded || 0;
            projectionValue = source?.amountToFund || 0;
        }
        const isFutureTimeline = new Date(period.periodStartDate) > new Date();

        const periodDisplayValue = getSourcesPeriodDisplayValue(
            fundedValue, projectionValue, periodIndex, inCurrentPeriodIndex, isFutureTimeline,
        );

        const cellKey = `${periodCadence}-${period.periodName}-${period.periodStartDate}-${traunchIndex}-${sourceIndex ?? 'subtotal'}`;

        return (
            <PeriodCellValue
                key={cellKey}
                tooltipId={cellKey}
                period={{ label: period?.periodName, value: period?.periodName }}
                value={numeralFormatterCurrency(periodDisplayValue, showCentsFormat)}
                cellClassName="NSTable__tbody__tr__td text-dark text-right"
            />
        );
    });

    const rowKey = firstPeriodSource
        ? `${periodCadence}-${firstPeriodSource.name}-${traunchIndex}-${sourceIndex}`
        : `${periodCadence}-sourceTraunch-${traunchIndex}`;

    return (
        <tr
            key={rowKey}
            className={classNames('text-dark', {
                NSTable__thead__tr: !firstPeriodSource,
                NSTable__tbody__tr: firstPeriodSource,
                'font-weight-bold': !firstPeriodSource,
            })}
        >
            {/* source row */}
            <RenderIf isTrue={firstPeriodSource}>
                <td className="NSTable__tbody__tr__td">
                    <div className="NSTable__tbody__tr__td__content">
                        {firstPeriodSource?.name}
                    </div>
                </td>
                <td className="NSTable__tbody__tr__td" />
                <td className="NSTable__tbody__tr__td" />
                <td className="NSTable__tbody__tr__td">
                    <div className="NSTable__tbody__tr__td__content">
                        {firstPeriodTraunchData.traunch}
                    </div>
                </td>
                <td className="NSTable__tbody__tr__td">
                    <div className="NSTable__tbody__tr__td__content text-right">
                        {numeralFormatterCurrency(firstPeriodSource?.amount || 0, showCentsFormat)}
                    </div>
                </td>
                <td className="NSTable__tbody__tr__td">
                    <div className="NSTable__tbody__tr__td__content text-right">
                        {percentFormatter(Number(firstPeriodSource?.proRataSourceShareOfTraunch) * 100)}
                    </div>
                </td>
            </RenderIf>

            {/* traunch subtotal row */}
            <RenderIf isTrue={!firstPeriodSource && firstPeriodTraunchData}>
                <td className="NSTable__tbody__tr__td">
                    {`Subtotal [Funding traunch ${firstPeriodTraunchData?.traunch}]`}
                </td>
                <td className="NSTable__tbody__tr__td" />
                <td className="NSTable__tbody__tr__td" />
                <td className="NSTable__tbody__tr__td" />
                <td className="NSTable__tbody__tr__td">
                    <div className="NSTable__tbody__tr__td__content text-right">
                        {numeralFormatterCurrency(firstPeriodTraunchData?.amount || 0, showCentsFormat)}
                    </div>
                </td>
                <td className="NSTable__tbody__tr__td" />
            </RenderIf>

            {getPeriodCells().filter(Boolean)}
        </tr>
    );
};

export default ProjectActualsSourceRow;
